import type { CampaignBanner, StandRow } from "../../../models/TrendDisplay";
import type {
	MillVisualizerData,
	VisualizerBillet,
	VisualizerStand,
	VisualizerStatus,
} from "../models/MillVisualizer";

const EMPTY: MillVisualizerData = {
	millName: "Roanoke Bar Mill",
	billetCount: 0,
	globalStatus: "idle",
	stands: [],
	billets: [],
};

const deriveStatus = (s: StandRow): VisualizerStatus => {
	if (s.Active === false) return "idle";
	if (s.IsBilletPresent === true) return "rolling";
	return "idle";
};

// Project the filtered StandRow[] + the precomputed VisualizerBillet[] (from
// mapInFlightBillets) into the visualizer's data shape. No per-stand billet
// identity — that concept is gone. Each VisualizerBillet already carries its
// own range; this function just builds the per-stand metadata and packages
// the result.
export const mapToVisualizerData = (
	visibleStands: StandRow[],
	billets: VisualizerBillet[],
	_banner: CampaignBanner | null,
): MillVisualizerData => {
	if (visibleStands.length === 0) return EMPTY;

	const visualizerStands: VisualizerStand[] = visibleStands.map((s, i) => {
		// Per-stand BIL display: show the billet whose range covers this
		// stand position. Match the capsule's visual extent — if the bar
		// is drawn across this slot, the label tags the bar's identity.
		// This stays consistent with the capsule rendering and avoids the
		// "bar visible but BIL —" inconsistency.
		const owning = billets.find(
			(b) => b.firstStandPosition <= i + 1 && i + 1 <= b.lastStandPosition,
		);
		return {
			id: `stand-${s.Stand}-${i}`,
			name: s.Stand,
			position: i + 1,
			status: deriveStatus(s),
			rFactorCurrent: s.RFactorCurrent,
			rFactorTarget: s.RFactorTarget,
			rFactorMin: s.RFactorMin,
			rFactorMax: s.RFactorMax,
			rFactorComparisonEnabled: s.EnabledComparison,
			currentA: s.Current,
			speedMps: s.Speed,
			gapMm: s.Gap,
			entryTempC: null,
			currentBilletId: owning?.billetId ?? null,
			heatNumber: owning?.heatNumber ?? null,
			expectedSection: "round",
			isCBT: s.Stand?.toUpperCase() === "CBT",
		};
	});

	return {
		millName: "Roanoke Bar Mill",
		billetCount: billets.length,
		globalStatus: billets.length > 0 ? "rolling" : "idle",
		stands: visualizerStands,
		billets,
	};
};
